import React, { useState, useEffect, useRef, useContext } from "react";
import {
	Container,
	Row,
	Col,
	Card,
	Alert,
	CardBody,
	Button,
	Label,
	Input,
	FormFeedback,
	Form,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { getCountry, getStateByCountryId } from "../../api/countryService";
import { UserProfileData, updateProfile } from "../../api/userProfileService";
import { uploadProfileImage } from "../../api/userService";
import { uploadUserProfile } from "../../api/uploadDocumentService";
import withRouter from "../../components/Common/withRouter";
import { resetProfileFlag } from "../../store/actions";
import { createSelector } from "reselect";
import "../../assets/css/userprofile.css";
import Select from "react-select";
import { Link } from "react-router-dom";
import FullScreenLoader from "../../components/Common/Loader";
import CommonToastContainer from "../../components/Common/CommonToastContainer";
import AuthContext from "../../Context/AuthContext";

const UserProfile = () => {
	document.title = "Profile";


	const dispatch = useDispatch();
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [countries, setCountries] = useState({ Countries: [] });
	const [states, setStates] = useState([]);
	const [customerId, setCustomerId] = useState("");
	const [showLoader, setShowLoader] = useState(false);
	const [toasts, setToasts] = useState([]);

	const userprofilepage = createSelector(
		(state) => state.profile,
		(state) => ({
			error: state.error,
			success: state.success,
		})
	);

	const { error, success } = useSelector(userprofilepage);

	const showToast = (toastData) => {
		setToasts((prev) => [...prev, { id: Date.now() + Math.random(), ...toastData }]);
	};

	const removeToast = (id) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	useEffect(() => {
		const authUser = localStorage.getItem("authUser");
		if (authUser) {
			const obj = JSON.parse(authUser);
			setName(obj?.username || "");
			setEmail(obj?.email || "");
			setTimeout(() => dispatch(resetProfileFlag()), 3000);
		}
	}, [dispatch, success]);

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			firstName: "",
			lastName: "",
			companyName: "",
			username: name || "",
			password: "",
			confirmPassword: "",
			email: email || "",
			phone: "",
			businessType: "",
			address: "",
			country: "",
			state: "",
			city: "",
			zipCode: "",
		},
		validationSchema: Yup.object({
			firstName: Yup.string().required("Please enter your First Name"),
			lastName: Yup.string().required("Please enter your Last Name"),
			companyName: Yup.string().required("Please enter your Company Name"),
			email: Yup.string().email("Invalid email format").required("Please enter your Email"),
			phone: Yup.string().required("Please enter your Phone Number"),
			businessType: Yup.string().required("Please select your Business Type"),
			address: Yup.string().required("Please enter your Address"),
			country: Yup.string().required("Please select your Country"),
			state: Yup.string().required("Please select your State"),
			city: Yup.string().required("Please enter your City"),
			zipCode: Yup.string().required("Please enter your Zip Code"),
		}),
		onSubmit: async (values, { setSubmitting }) => {
			setShowLoader(true);
			try {
				const payload = {
					Customer_ID: String(customerId || ""),
					FirstName: values.firstName,
					LastName: values.lastName,
					CompanyName: values.companyName,
					Country_ID: values.country?.toString(),
					State_ID: values.state?.toString(),
					City: values.city,
					Address: values.address,
					MobileNo: values.phone,
					PhoneNo: "",
					Email: values.email,
					BusinessType: values.businessType,
					ZipCode: values.zipCode,
				};

				const response = await updateProfile(payload);

				if (response?.Message?.[0]?.RETURNCODE === "1") {
					showToast({
						title: "Success!",
						message: response?.Message?.[0]?.RETURNMESSAGE || "Profile updated successfully!",
						type: "success",
					});
				} else {
					showToast({
						title: "Error!",
						message: response?.Message?.[0]?.RETURNMESSAGE || "Failed to update profile",
						type: "error",
					});
				}
			} catch (error) {
				console.error("Update Profile API Error:", error);
				showToast({
					title: "Error!",
					message: "Something went wrong while updating profile.",
					type: "error",
				});
			} finally {
				setShowLoader(false);
				setSubmitting(false);
			}
		},
	});

	const fetchCountries = async () => {
		try {
			const response = await getCountry();
			if (response?.Message?.[0]?.RETURNCODE === "1") {
				setCountries(response || { Countries: [] });
			} else {
				showToast({
					title: "Error!",
					message: response?.Message?.[0]?.RETURNMESSAGE || "Country not fetched!",
					type: "error",
				});
			}
		} catch (error) {
			console.error("Country fetch error:", error);
			showToast({
				title: "Error!",
				message: "Something went wrong while fetching country data.",
				type: "error",
			});
		}
	};

	useEffect(() => {
		fetchCountries();
	}, []);

	const fetchUserProfile = async () => {
		try {
			const response = await UserProfileData();

			if (response?.Message?.[0]?.RETURNCODE === "1") {
				if (response?.UserDetail?.[0]) {
					const profile = response.UserDetail[0];

					setCustomerId(profile.CUSTOMER_ID || "");

					validation.setValues({
						firstName: profile.FIRSTNAME || "",
						lastName: profile.LASTNAME || "",
						companyName: profile.COMPANYNAME || "",
						username: profile.USERNAME || "",
						email: profile.EMAIL || "",
						phone: profile.MOBILENO || "",
						businessType: profile.BUSINESSTYPE || "",
						address: profile.ADDRESS || "",
						country: profile.COUNTRY_ID || "",
						state: profile.STATE_ID || "",
						city: profile.CITY || "",
						zipCode: profile.ZIPCODE || "",
						password: "",
						confirmPassword: "",
					});

					if (profile.COUNTRY_ID) {
						try {
							const stateResponse = await getStateByCountryId({
								IntCountyID: profile.COUNTRY_ID,
							});

							if (stateResponse?.Message?.[0]?.RETURNCODE === "1") {
								setStates(stateResponse?.States || []);
							} else {
								showToast({
									title: "Error!",
									message: stateResponse?.Message?.[0]?.RETURNMESSAGE || "Failed to load states",
									type: "error",
								});
							}
						} catch (stateErr) {
							console.error("Error fetching states:", stateErr);
							showToast({
								title: "Error!",
								message: "Failed to load states",
								type: "error",
							});
						}
					}
				} else {
					showToast({
						title: "Information!",
						message: "No profile details found for this user",
						type: "info",
					});
				}
			} else {
				showToast({
					title: "Error!",
					message: response?.Message?.[0]?.RETURNMESSAGE || "Profile detail not fetched properly",
					type: "error",
				});
			}
		} catch (error) {
			console.error("Error fetching user profile:", error);
			showToast({
				title: "Error!",
				message: "Error fetching user profile",
				type: "error",
			});
		}
	};

	useEffect(() => {
		fetchUserProfile();
	}, []);

	const selectedCountryOption =
		countries?.Countries?.find((r) => String(r.COUNTRY_ID) === String(validation.values.country))
			? {
				value: validation.values.country,
				label: countries.Countries.find(
					(r) => String(r.COUNTRY_ID) === String(validation.values.country)
				)?.COUNTRYNAME,
			}
			: null;

	const selectedStateOption =
		states.find((r) => String(r.STATE_ID) === String(validation.values.state))
			? {
				value: validation.values.state,
				label: states.find((r) => String(r.STATE_ID) === String(validation.values.state))
					?.STATENAME,
			}
			: null;

	return (
		<>
			<div
				className="bg-pattern d-flex flex-column"
				style={{
					backgroundImage: `linear-gradient(rgba(0,0,0,.30), rgba(0,0,0,.30)), url(/assets/images/backgroundImages/bg3.png)`,
					backgroundSize: "cover",
				}}
			>
				{showLoader && <FullScreenLoader />}

				<div className="account-pages flex-grow-1 main-background-register">
					<Container>
						<Row className="justify-content-center mt-4">
							<Col lg={6} md={8} xl={6}>
								<Card
									style={{
										borderRadius: "28px",
										overflow: "hidden",
										backgroundColor: "#ffffff",
									}}
								>
									<CardBody className="p-4 register-background">
										<div className="text-center mb-3">
											<h4 className="fw-bold mb-1 profile-heading">Your Profile</h4>
										</div>

										<div className="text-center mb-3">
											<ProfileImageUploader showToast={showToast} />
											<h4 className="fw-bold mb-1">{name}</h4>
											<p className="text-muted mb-0">{email}</p>
										</div>

										{error && <Alert color="danger">{error}</Alert>}
										{success && <Alert color="success">{success}</Alert>}

										<Form className="form-horizontal" onSubmit={validation.handleSubmit}>
											<Row>
												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="firstName">
															First Name
														</Label>
														<Input
															id="firstName"
															name="firstName"
															type="text"
															placeholder="Enter First Name"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.firstName}
															invalid={validation.touched.firstName && !!validation.errors.firstName}
														/>
														<FormFeedback>{validation.errors.firstName}</FormFeedback>
													</div>
												</Col>

												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="lastName">
															Last Name
														</Label>
														<Input
															id="lastName"
															name="lastName"
															type="text"
															placeholder="Enter Last Name"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.lastName}
															invalid={validation.touched.lastName && !!validation.errors.lastName}
														/>
														<FormFeedback>{validation.errors.lastName}</FormFeedback>
													</div>
												</Col>

												<Col md={12}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="companyName">
															Company Name
														</Label>
														<Input
															id="companyName"
															name="companyName"
															type="text"
															placeholder="Enter Company Name"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.companyName}
															invalid={
																validation.touched.companyName &&
																!!validation.errors.companyName
															}
														/>
														<FormFeedback>{validation.errors.companyName}</FormFeedback>
													</div>
												</Col>
											</Row>

											<Row>
												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="username">
															Username
														</Label>
														<Input
															id="username"
															name="username"
															type="text"
															placeholder="Enter Username"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.username}
															invalid={validation.touched.username && !!validation.errors.username}
														/>
														<FormFeedback>{validation.errors.username}</FormFeedback>
													</div>
												</Col>

												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="email">
															Email
														</Label>
														<Input
															id="email"
															name="email"
															type="email"
															placeholder="Enter Email"
															value={validation.values.email}
															disabled
														/>
														<FormFeedback>{validation.errors.email}</FormFeedback>
													</div>
												</Col>
											</Row>

											<Row>
												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="phone">
															Mobile Number
														</Label>
														<Input
															id="phone"
															name="phone"
															type="text"
															placeholder="Enter Phone Number"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.phone}
															invalid={validation.touched.phone && !!validation.errors.phone}
														/>
														<FormFeedback>{validation.errors.phone}</FormFeedback>
													</div>
												</Col>

												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="businessType">
															Business Type
														</Label>
														<Input
															id="businessType"
															name="businessType"
															type="select"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.businessType}
															invalid={
																validation.touched.businessType &&
																!!validation.errors.businessType
															}
														>
															<option value="">Select Business Type</option>
															<option value="Wholesaler">Wholesaler</option>
															<option value="Retailer">Retailer</option>
															<option value="Others">Others</option>
														</Input>
														<FormFeedback>{validation.errors.businessType}</FormFeedback>
													</div>
												</Col>
											</Row>

											<div className="mb-2">
												<Label className="form-label-login" htmlFor="address">
													Address
												</Label>
												<Input
													id="address"
													name="address"
													type="textarea"
													placeholder="Enter Address"
													onChange={validation.handleChange}
													onBlur={validation.handleBlur}
													value={validation.values.address}
													invalid={validation.touched.address && !!validation.errors.address}
												/>
												<FormFeedback>{validation.errors.address}</FormFeedback>
											</div>

											<Row>
												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="country">
															Country
														</Label>
														<Select
															inputId="country"
															name="country"
															options={
																Array.isArray(countries?.Countries)
																	? countries.Countries.map((country) => ({
																		value: country.COUNTRY_ID,
																		label: country.COUNTRYNAME,
																	}))
																	: []
															}
															isSearchable
															isClearable
															placeholder="Select Country"
															value={selectedCountryOption}
															onChange={async (selectedOption) => {
																const selectedCountryId = selectedOption ? selectedOption.value : "";
																validation.setFieldValue("country", selectedCountryId || "");
																validation.setFieldValue("state", "");
																setStates([]);

																if (selectedCountryId) {
																	try {
																		const response = await getStateByCountryId({
																			IntCountyID: selectedCountryId,
																		});

																		if (response?.Message?.[0]?.RETURNCODE === "1") {
																			setStates(response?.States || []);
																		} else {
																			showToast({
																				title: "Error!",
																				message:
																					response?.Message?.[0]?.RETURNMESSAGE ||
																					"Failed to load states",
																				type: "error",
																			});
																		}
																	} catch (error) {
																		console.error("Error fetching states:", error);
																		setStates([]);
																		showToast({
																			title: "Error!",
																			message: "Failed to load states",
																			type: "error",
																		});
																	}
																}
															}}
															onBlur={() => validation.setFieldTouched("country", true)}
															className={
																validation.touched.country && validation.errors.country
																	? "is-invalid"
																	: ""
															}
															styles={{
																control: (provided) => ({
																	...provided,
																	backgroundColor: "#f3f3f4",
																	border: "none",
																}),
															}}
														/>
														{validation.touched.country && validation.errors.country && (
															<div className="invalid-feedback d-block">
																{validation.errors.country}
															</div>
														)}
													</div>
												</Col>

												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="state">
															State
														</Label>
														<Select
															inputId="state"
															name="state"
															options={
																Array.isArray(states)
																	? states.map((state) => ({
																		value: state.STATE_ID,
																		label: state.STATENAME,
																	}))
																	: []
															}
															isSearchable
															isClearable
															placeholder="Select State"
															value={selectedStateOption}
															onChange={(selectedOption) => {
																validation.setFieldValue(
																	"state",
																	selectedOption ? selectedOption.value : ""
																);
															}}
															onBlur={() => validation.setFieldTouched("state", true)}
															className={
																validation.touched.state && validation.errors.state
																	? "is-invalid"
																	: ""
															}
															styles={{
																control: (provided) => ({
																	...provided,
																	backgroundColor: "#f3f3f4",
																	border: "none",
																}),
															}}
														/>
														{validation.touched.state && validation.errors.state && (
															<div className="invalid-feedback d-block">
																{validation.errors.state}
															</div>
														)}
													</div>
												</Col>
											</Row>

											<Row>
												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="city">
															City
														</Label>
														<Input
															id="city"
															name="city"
															type="text"
															placeholder="Enter City"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.city}
															invalid={validation.touched.city && !!validation.errors.city}
														/>
														<FormFeedback>{validation.errors.city}</FormFeedback>
													</div>
												</Col>

												<Col md={6}>
													<div className="mb-2">
														<Label className="form-label-login" htmlFor="zipCode">
															Zip Code
														</Label>
														<Input
															id="zipCode"
															name="zipCode"
															type="text"
															placeholder="Enter Zip Code"
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.zipCode}
															invalid={validation.touched.zipCode && !!validation.errors.zipCode}
														/>
														<FormFeedback>{validation.errors.zipCode}</FormFeedback>
													</div>
												</Col>
											</Row>

											<div className="d-grid mt-4">
												<Button
													type="submit"
													color="primary"
													className="px-5 py-2 fw-semibold rounded-3"
													disabled={validation.isSubmitting}
												>
													{validation.isSubmitting ? "Updating..." : "Save Changes"}
												</Button>
											</div>

											<div className="text-center mt-3">
												<Link to="/change-password" className="text-primary fw-semibold">
													Change Password
												</Link>
											</div>
										</Form>
									</CardBody>
								</Card>
							</Col>
						</Row>
					</Container>
				</div>
			</div>

			<CommonToastContainer toasts={toasts} removeToast={removeToast} />
		</>
	);
};

const ProfileImageUploader = ({ showToast }) => {
	const authContext = useContext(AuthContext);
	const profileImage = authContext?.user?.PROFILEIMAGE;
	const updateAuthProfileImage = authContext?.updateProfileImage;

	const [selectedImage, setSelectedImage] = useState(null);
	const [previewImage, setPreviewImage] = useState(null);
	const [isUploading, setIsUploading] = useState(false);

	const fileInputRef = useRef(null);
	const userImg = "../../assets/images/users/userprofile.jpg"; 
	console.log(profileImage, userImg)
	const defaultProfileImage = profileImage || userImg;

	const triggerFileInput = () => {
		fileInputRef.current?.click();
	};

	const handleImageChange = (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		if (!file.type.startsWith("image/")) {
			showToast?.({
				title: "Warning!",
				message: "Please select a valid image file.",
				type: "warning",
			});
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			showToast?.({
				title: "Warning!",
				message: "Image size should be less than 5 MB.",
				type: "warning",
			});
			return;
		}

		setSelectedImage(file);
		setPreviewImage(URL.createObjectURL(file));
	};

	const handleUploadProfileImage = async () => {
		if (!selectedImage) {
			showToast?.({
				title: "Warning!",
				message: "Please select an image first.",
				type: "warning",
			});
			return;
		}

		try {
			setIsUploading(true);

			const formData = new FormData();
			formData.append("file", selectedImage);

			const uploadRes = await uploadUserProfile(formData);

			const filePath =
				uploadRes?.filePath ||
				uploadRes?.FilePath ||
				uploadRes?.ROW?.[0]?.FILEPATH;

			if (!filePath) {
				throw new Error("File upload failed.");
			}

			const response = await uploadProfileImage({
				ImageURL: filePath,
			});

			if (response?.Message?.[0]?.RETURNCODE === "1") {
				const newProfileImage =
					response?.ImageDetail?.[0]?.COMPANYLOGO || filePath;

				if (newProfileImage && typeof updateAuthProfileImage === "function") {
					updateAuthProfileImage(newProfileImage);
				}

				showToast?.({
					title: "Success!",
					message: "Profile image updated successfully.",
					type: "success",
				});
			} else {
				showToast?.({
					title: "Error!",
					message:
						response?.Message?.[0]?.RETURNMESSAGE || "Image update failed.",
					type: "error",
				});
			}
		} catch (error) {
			console.error("Profile image upload error:", error);
			showToast?.({
				title: "Error!",
				message: "Something went wrong while uploading the profile image.",
				type: "error",
			});
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<div className="profile_upload_section">
			<div
				className="profile_img"
				onClick={triggerFileInput}
				style={{ cursor: "pointer" }}
			>
				<img
					src={previewImage || defaultProfileImage}
					className="profile-pic"
					alt="User Profile"
					onError={(e) => {
						e.currentTarget.src = userImg;
					}}
				/>
			</div>

			<input
				type="file"
				ref={fileInputRef}
				accept=".png,.jpg,.jpeg"
				onChange={handleImageChange}
				style={{ display: "none" }}
			/>

			<div className="profile_update_btn_container">
				<button
					type="button"
					className="fw-semibold rounded-3 btn btn-primary"
					onClick={handleUploadProfileImage}
					disabled={isUploading}
				>
					{isUploading ? "Uploading..." : "Update Profile"}
				</button>
			</div>
		</div>
	);
};

export default withRouter(UserProfile);